import type { ChangeEvent, FocusEvent, InputHTMLAttributes, ReactNode } from 'react'
import { Field, Input, Label, ValidationMessage, type InputProps } from '@digdir/designsystemet-react'
import type { FieldSize } from './Select'
import { HelpText } from './HelpText'

// ---------------------------------------------------------------------------
// TextField — presentational text input wrapper.
//
// Uses Designsystemet controls with the existing form and layout contract.
// Props in → UI out; no data fetching, no auth logic. Safe for all surfaces
// including the public, sessionless register bundle.
//
// DOM structure:
//   <div class="field">
//     <label class="field__lbl" for={id}>…</label>
//     [<span class="field__hint">…</span>]
//     <input class="input" id={id} name={name} aria-invalid …/>
//     [<span id="{id}-err" class="field__err">…</span>]
//   </div>
// ---------------------------------------------------------------------------

export type TextFieldProps = {
  /** Visible label text. Already i18n'd by the caller — no Norwegian strings here. */
  label: string
  labelActions?: ReactNode
  /** Keep the accessible name while hiding the visible label. */
  hideLabel?: boolean
  placeholder?: string
  /** Current controlled value. */
  value: string
  /** Change handler; receives the synthetic change event. */
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  /** Optional blur handler — e.g. to validate or look a value up on leave. */
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void
  /** HTML id for the input — also used to associate <label> via htmlFor. */
  id: string
  /** name attribute forwarded to the input. */
  name: string
  /** input type (text, email, number, search, tel, url, password, …). */
  type: NonNullable<InputProps['type']>
  /** Optional hint shown below the label, above the input. */
  hint?: string
  help?: string
  helpLabel?: string
  /** Validation error message. When set, input gets aria-invalid="true". */
  error?: string
  /** Marks the input as required. */
  required?: boolean
  /** Disables the input. */
  disabled?: boolean
  /**
   * Renders the value as read-only: still focusable, still in the tab order,
   * and announced as read-only by assistive tech — unlike `disabled`, which
   * hides the value from keyboard and screen-reader users entirely. Use for a
   * value the user may read and copy but not change here (e.g. one owned by an
   * external register).
   */
  readOnly?: boolean
  /** Optional min attribute (e.g. for date or numeric inputs). */
  min?: string
  /** Optional max attribute (e.g. for date inputs). */
  max?: string
  step?: string
  /** On-screen keyboard hint for mobile (e.g. `numeric` for a digit field). */
  inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode']
  /** autocomplete attribute — set `off` for values a browser should not fill. */
  autoComplete?: string
  /** Optional content-width cap (desktop only). Default: full width. */
  size?: FieldSize
}

/**
 * Presentational text field component. Renders a labelled input with optional
 * hint and error messaging. Label association and aria-invalid are handled
 * automatically from props.
 *
 * Usage:
 *   <TextField
 *     id="applicant-email"
 *     name="applicant[email]"
 *     label={t('fields.email')}
 *     type="email"
 *     value={email}
 *     onChange={e => setEmail(e.target.value)}
 *     hint={t('fields.email_hint')}
 *     error={errors.email}
 *     required
 *   />
 */
export function TextField({
  label,
  labelActions,
  hideLabel = false,
  placeholder,
  value,
  onChange,
  onBlur,
  id,
  name,
  type,
  hint,
  help,
  helpLabel,
  error,
  required = false,
  disabled = false,
  readOnly = false,
  min,
  max,
  step,
  inputMode,
  autoComplete,
  size,
}: TextFieldProps) {
  const errorId = error ? `${id}-err` : undefined
  const hintId = hint ? `${id}-hint` : undefined

  return (
    <Field className={size ? `field field--w-${size}` : 'field'}>
      <div className="field__heading"><Label className={hideLabel ? 'sr-only' : 'field__lbl'} htmlFor={id}>
        {label}
      </Label>{help && <HelpText label={label} triggerLabel={helpLabel} text={help} />}{labelActions}</div>

      {hint && <Field.Description id={hintId} className="field__hint">{hint}</Field.Description>}

      <Input
        className="input"
        id={id}
        name={name}
        type={type}
        placeholder={placeholder}
        min={min}
        max={max}
        step={step}
        inputMode={inputMode}
        autoComplete={autoComplete}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        aria-invalid={error ? 'true' : undefined}
      />

      {error && (
        <ValidationMessage id={errorId} className="field__err">
          {error}
        </ValidationMessage>
      )}
    </Field>
  )
}
